// Import the CSS file - this is required for Vite to process it
import '../css/main.css';
import useToast from './toast.js';
import initQuickCart from './quick-cart.js';
import searchjs from './search.js';
import initCategoryMenu from './category-menu.js';

// Import Swiper and its modules for product pages
import { Swiper } from 'swiper';
import { Navigation, Pagination, Thumbs } from 'swiper/modules';
import 'swiper/css';

// Initialize Swiper globally
if (typeof window !== 'undefined') {
    window.Swiper = Swiper;
    window.Navigation = Navigation;
    window.Pagination = Pagination;
    window.Thumbs = Thumbs;
}

// Make useToast available globally
window.useToast = useToast;

/**
 * Update WooCommerce fragments without jQuery
 * Replaces elements in the DOM with new HTML from fragments
 */
function updateFragments(fragments) {
    Object.keys(fragments).forEach(selector => {
        const newHtml = fragments[selector];
        
        // Handle different selector types
        let elements;
        try {
            elements = document.querySelectorAll(selector);
        } catch {
            // If selector is invalid, skip
            return;
        }
        
        elements.forEach(element => {
            // Create a temporary container to parse the new HTML
            const temp = document.createElement('div');
            temp.innerHTML = newHtml;
            const newElement = temp.firstElementChild;
            
            if (newElement) {
                element.replaceWith(newElement);
            } else {
                // If no element, just update innerHTML
                element.innerHTML = newHtml;
            }
        });
    });
}

function AjaxAddToCart() {
    document.body.addEventListener('click', function(e) {
        const addToCartButton = e.target.closest('.ajax_add_to_cart');
        
        if (addToCartButton) {
            e.preventDefault();
            
            // Get product ID from various possible sources
            let productId = addToCartButton.getAttribute('data-product_id') || 
                           addToCartButton.getAttribute('value') ||
                           (addToCartButton.getAttribute('name') === 'add-to-cart' ? addToCartButton.getAttribute('value') : null);
            
            // For forms, also check hidden input with name="add-to-cart"
            if (!productId) {
                const form = addToCartButton.closest('form');
                if (form) {
                    const hiddenInput = form.querySelector('input[name="add-to-cart"]');
                    if (hiddenInput) {
                        productId = hiddenInput.value;
                    }
                }
            }
            
            // Get quantity from data attribute or nearby quantity input
            let quantity = addToCartButton.getAttribute('data-quantity');
            
            if (!quantity) {
                const form = addToCartButton.closest('form');
                if (form) {
                    const quantityInput = form.querySelector('input[name="quantity"]');
                    quantity = quantityInput ? quantityInput.value : 1;
                }
            }
            
            quantity = quantity || 1;
            
            if (!productId) {
                useToast('Błąd: Nie można znaleźć ID produktu', 'error');
                return;
            }
            
            addToCartButton.classList.add('loading');
            addToCartButton.disabled = true;
            
            // Handle different loading states
            const loadingText = addToCartButton.querySelector('.ajax-loading-text');
            const loadingSpinner = addToCartButton.querySelector('.ajax-loading-spinner');
            const loadingIndicator = addToCartButton.querySelector('.loading-indicator');
            const buttonText = addToCartButton.querySelector('.button-text');
            
            // Show loading state for single product page
            if (loadingText && loadingSpinner) {
                loadingText.classList.add('hidden');
                loadingSpinner.classList.remove('hidden');
            }
            
            // Show loading state for loop products
            if (loadingIndicator && buttonText) {
                buttonText.textContent = 'Dodawanie...';
                loadingIndicator.classList.remove('hidden');
            }
            
            fetch(wc_add_to_cart_params.wc_ajax_url.toString().replace('%%endpoint%%', 'add_to_cart'), {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/x-www-form-urlencoded',
                },
                body: `product_id=${productId}&quantity=${quantity}`
            })
            .then(response => response.json())
            .then(data => {
                if (data.error) {
                    useToast(data.message || 'Błąd podczas dodawania produktu do koszyka', 'error');
                } else {
                    useToast('Produkt dodany do koszyka', 'success');
                    
                    // Update fragments using vanilla JavaScript
                    if (data.fragments) {
                        updateFragments(data.fragments);
                        extractCartCountFromFragments(data.fragments);
                    }
                    
                    if (data.cart_count !== undefined) {
                        updateMiniCartCount(data.cart_count);
                    }
                    
                    // Trigger native events
                    document.body.dispatchEvent(new CustomEvent('wc_fragment_refresh'));
                    document.body.dispatchEvent(new CustomEvent('added_to_cart', { 
                        detail: { fragments: data.fragments, cart_hash: data.cart_hash } 
                    }));
                    
                    // Trigger jQuery event for plugins like PixelYourSite
                    if (typeof jQuery !== 'undefined') {
                        jQuery(document.body).trigger('added_to_cart', [data.fragments, data.cart_hash, addToCartButton]);
                    }
                }
            })
            .catch(error => {
                useToast('Błąd podczas dodawania produktu do koszyka. Spróbuj ponownie.', 'error');
                console.error('Error:', error);
            })
            .finally(() => {
                addToCartButton.classList.remove('loading');
                addToCartButton.disabled = false;
                
                // Reset loading state for single product page
                if (loadingText && loadingSpinner) {
                    loadingText.classList.remove('hidden');
                    loadingSpinner.classList.add('hidden');
                }
                
                // Reset loading state for loop products
                if (loadingIndicator && buttonText) {
                    buttonText.textContent = 'Dodaj do koszyka';
                    loadingIndicator.classList.add('hidden');
                }
            });
        }
    });
}

function extractCartCountFromFragments(fragments) {
    if (fragments['div.widget_shopping_cart_content']) {
        const html = fragments['div.widget_shopping_cart_content'];
        const tempDiv = document.createElement('div');
        tempDiv.innerHTML = html;
        
        const cartItems = tempDiv.querySelectorAll('.woocommerce-mini-cart-item');
        let totalItems = 0;
        
        cartItems.forEach(item => {
            const quantityElement = item.querySelector('.quantity');
            if (quantityElement) {
                const quantityText = quantityElement.textContent;
                const quantity = parseInt(quantityText.split('×')[0].trim());
                if (!isNaN(quantity)) {
                    totalItems += quantity;
                }
            } else {
                totalItems += 1; 
            }
        });
        
        updateMiniCartCount(totalItems);
    }
}

function updateMiniCartCount(count) {
    const miniCartCountElements = document.querySelectorAll('.cart-count, .cart-contents-count, .view-cart span');
    miniCartCountElements.forEach(element => {
        element.textContent = count.toString().trim();
        
        if (parseInt(count) > 0 && element.classList.contains('hidden')) {
            element.classList.remove('hidden');
        } else if (parseInt(count) === 0 && !element.classList.contains('hidden')) {
            element.classList.add('hidden');
        }
    });
}

function footerSlider() {
    const wrapper = document.getElementById('reviewsSliderWrapper');
    if (!wrapper) return;
    
    const prevBtn = document.getElementById('prevBtn');
    const nextBtn = document.getElementById('nextBtn');
    
    // Find the posts container from the reviews-feed plugin
    const feedPosts = wrapper.querySelector('.sb-feed-posts');
    if (!feedPosts) return;
    
    // Get all review items
    const slides = feedPosts.querySelectorAll('.sb-post-item-wrap');
    if (slides.length === 0) return;
    
    const totalSlides = slides.length;
    let currentSlide = 0;
    
    // Set up slider styles
    feedPosts.style.display = 'flex';
    feedPosts.style.transition = 'transform 0.3s ease-in-out';
    feedPosts.style.width = '100%';
    
    // Make each slide full width
    slides.forEach(slide => {
        slide.style.flex = '0 0 100%';
        slide.style.width = '100%';
        slide.style.boxSizing = 'border-box';
    });

    function updateSlider() {
        const translateX = -currentSlide * 100;
        feedPosts.style.transform = `translateX(${translateX}%)`;
    }

    function nextSlide() {
        currentSlide = (currentSlide + 1) % totalSlides;
        updateSlider();
    }

    function prevSlide() {
        currentSlide = (currentSlide - 1 + totalSlides) % totalSlides;
        updateSlider();
    }

    // Event listeners
    if (nextBtn) nextBtn.addEventListener('click', nextSlide);
    if (prevBtn) prevBtn.addEventListener('click', prevSlide);

    // Initialize
    updateSlider();
}

document.addEventListener('DOMContentLoaded', function() {
    AjaxAddToCart();
    const quickCartInstance = initQuickCart(nautilusData);
    window.nautilusQuickCart = quickCartInstance;
    
    const categoryMenuInstance = initCategoryMenu();
    window.nautilusCategoryMenu = categoryMenuInstance;
    
    searchjs();
    footerSlider();
});
